import { useRef, useState } from "react";

import { PlayerAvatar } from "@/components/PlayerAvatar";
import { Field, StatusBanner } from "@/components/ui";
import { Tetromino } from "@/components/Tetromino";
import { pieceColorVar, type PieceType } from "@/game/pieces";
import {
  playersApi,
  uploadAvatar,
  type PlayerState,
  type SocialLink,
} from "@/lib/playersApi";
import { useSaveState } from "@/lib/useSaveState";

interface PlayerProfileEditorProps {
  readonly state: PlayerState;
  readonly onSaved: () => void;
}

const BLANK: SocialLink = { label: "", url: "" };

/**
 * The about-me editor for a member who has been assigned to a roster player.
 *
 * Deliberately shows nothing at all when the account is unassigned: the person
 * cannot fix that themselves, so a form they are not allowed to submit would be
 * worse than a sentence telling them who can.
 */
export function PlayerProfileEditor({
  state,
  onSaved,
}: PlayerProfileEditorProps) {
  const { profile, rules } = state;
  const [bio, setBio] = useState(profile?.bio ?? "");
  const [pronouns, setPronouns] = useState(profile?.pronouns ?? "");
  const [piece, setPiece] = useState<PieceType | null>(
    profile?.favourite_piece ?? null,
  );
  const [socials, setSocials] = useState<SocialLink[]>(
    profile?.socials.length ? [...profile.socials] : [BLANK],
  );
  const { saving, message, tone, run } = useSaveState();
  // Problems we can see without asking the server — an oversized file,
  // for one, which above the proxy limit comes back as unreadable HTML.
  const [localError, setLocalError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // Bumped when the picture is replaced. Seeded once per mount rather than
  // fixed at 0: the API caches avatars for five minutes, so a counter that
  // restarted at 0 on every remount meant "?v=0" was a cache hit and the member
  // saw their *old* picture right after being told the new one was saved.
  // Computing it per render is the other wrong answer — that re-requested the
  // image on every keystroke in the bio.
  const [avatarVersion, setAvatarVersion] = useState(() => Date.now());

  if (!state.assigned || !profile) {
    return (
      <div className="slab max-w-[58ch] px-6 py-5">
        <h2 className="mb-2 text-2xl">Your player profile</h2>
        <p className="leading-relaxed">
          Your account is not linked to a roster player yet. An officer has to
          make that link before you can write a profile — ask in the Discord and
          someone will sort it out.
        </p>
      </div>
    );
  }

  const patchSocial = (index: number, changes: Partial<SocialLink>) =>
    setSocials(
      socials.map((link, i) => (i === index ? { ...link, ...changes } : link)),
    );

  const save = async () => {
    const kept = socials.filter((link) => link.label.trim() || link.url.trim());
    const saved = await run(
      () =>
        playersApi.save({
          bio,
          pronouns,
          favourite_piece: piece,
          socials: kept,
        }),
      () => "Profile saved.",
    );
    if (saved) {
      setSocials(saved.socials.length ? [...saved.socials] : [BLANK]);
      onSaved();
    }
  };

  const pickAvatar = async (file: File | undefined) => {
    if (!file) return;
    // Checked here as well as on the server. Above the proxy's own limit the
    // rejection comes back as nginx's HTML error page rather than the API's
    // JSON, and the reader would be told only that the response was
    // unreadable — which is true and useless. An untrimmed phone photo is
    // exactly the file that lands in that band.
    if (file.size > rules.max_avatar_bytes) {
      const limit = Math.round(rules.max_avatar_bytes / 1024);
      const size = Math.round(file.size / 1024);
      setLocalError(
        `That picture is ${size} KB. The limit is ${limit} KB — try a smaller one.`,
      );
      if (fileInput.current) fileInput.current.value = "";
      return;
    }
    setLocalError(null);
    const saved = await run(
      () => uploadAvatar(file),
      () => "Picture saved.",
    );
    if (saved) {
      setAvatarVersion((version) => version + 1);
      onSaved();
    }
    if (fileInput.current) fileInput.current.value = "";
  };

  const dropAvatar = async () => {
    const saved = await run(
      () => playersApi.removeAvatar(),
      () => "Picture removed.",
    );
    if (saved) onSaved();
  };

  const maxKb = Math.round(rules.max_avatar_bytes / 1024);

  return (
    <div
      className="slab px-6 py-6"
      style={{ ["--slab-ink" as string]: pieceColorVar(piece ?? "T") }}
    >
      <div className="mb-1 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h2 className="text-2xl">Your player profile</h2>
        <span className="meta opacity-60">
          shown as {profile.roster_username}
        </span>
      </div>
      <p className="mb-6 max-w-[58ch] leading-relaxed opacity-70">
        This is what people see when they click your name on the roster.
      </p>

      {/* ── picture ── */}
      <div className="mb-7 flex flex-wrap items-center gap-5">
        <PlayerAvatar
          username={profile.roster_username}
          hasAvatar={profile.has_avatar}
          size="h-24 w-24"
          cell={14}
          version={avatarVersion}
          fallback={
            <span className="meta text-[0.625rem] opacity-40">No picture</span>
          }
        />

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            className="btn px-4 py-2 text-sm"
            onClick={() => fileInput.current?.click()}
            disabled={saving}
          >
            {profile.has_avatar ? "Replace picture" : "Upload picture"}
          </button>
          {profile.has_avatar ? (
            <button
              type="button"
              className="btn btn--ghost px-4 py-2 text-sm"
              onClick={dropAvatar}
              disabled={saving}
            >
              Remove
            </button>
          ) : null}
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/gif"
            className="hidden"
            onChange={(event) => pickAvatar(event.target.files?.[0])}
          />
          <p className="meta w-full text-[0.6875rem] opacity-55">
            PNG, JPEG or GIF, up to {maxKb} KB. No SVG.
          </p>
        </div>
      </div>

      {/* ── words ── */}
      <Field
        label="About you"
        htmlFor="profile-bio"
        hint={`${bio.length} / ${rules.max_bio_length}`}
        className="mb-5"
      >
        <textarea
          id="profile-bio"
          className="input min-h-[7rem]"
          maxLength={rules.max_bio_length}
          value={bio}
          onChange={(event) => setBio(event.target.value)}
          placeholder="How you play, what you are working on, anything you like."
        />
      </Field>

      <div className="mb-5 grid gap-4 sm:grid-cols-2">
        <Field label="Pronouns" htmlFor="profile-pronouns" hint="Optional.">
          <input
            id="profile-pronouns"
            className="input"
            maxLength={rules.max_pronouns_length}
            value={pronouns}
            onChange={(event) => setPronouns(event.target.value)}
          />
        </Field>

        <fieldset>
          <legend className="label mb-2">Favourite piece</legend>
          <div className="flex flex-wrap gap-1.5">
            {rules.pieces.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={piece === option}
                title={`${option} piece`}
                onClick={() => setPiece(piece === option ? null : option)}
                className={`flex h-10 w-10 items-center justify-center border-2 border-ink
                  transition-colors duration-100 ${piece === option ? "" : "bg-paper"}`}
                style={
                  piece === option
                    ? { background: pieceColorVar(option) }
                    : undefined
                }
              >
                <Tetromino type={option} cell={6} />
              </button>
            ))}
          </div>
        </fieldset>
      </div>

      {/* ── links ── */}
      <fieldset className="mb-6">
        <legend className="label mb-2">
          Links{" "}
          <span className="opacity-60">
            (https only, up to {rules.max_social_links})
          </span>
        </legend>
        <div className="flex flex-col gap-3">
          {socials.map((link, index) => (
            <div
              key={index}
              className="grid gap-2 sm:grid-cols-[10rem_1fr_auto]"
            >
              <input
                className="input"
                placeholder="Twitch"
                aria-label={`Link ${index + 1} label`}
                value={link.label}
                onChange={(event) =>
                  patchSocial(index, { label: event.target.value })
                }
              />
              <input
                className="input"
                placeholder="https://…"
                aria-label={`Link ${index + 1} address`}
                value={link.url}
                onChange={(event) =>
                  patchSocial(index, { url: event.target.value })
                }
              />
              <button
                type="button"
                className="btn btn--ghost px-3 py-2 text-xs"
                onClick={() =>
                  setSocials(socials.filter((_, i) => i !== index))
                }
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        {socials.length < rules.max_social_links ? (
          <button
            type="button"
            className="btn btn--ghost mt-3 px-3 py-2 text-xs"
            onClick={() => setSocials([...socials, BLANK])}
          >
            Add a link
          </button>
        ) : null}
      </fieldset>

      <StatusBanner
        tone={localError ? "error" : tone}
        message={localError ?? message}
        prefix={null}
      />

      <div className="mt-4">
        <button type="button" className="btn" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save profile"}
        </button>
      </div>
    </div>
  );
}
